/* @jsxImportSource react */
'use client';

import {
  type AriaAttributes,
  createContext,
  forwardRef,
  type ReactNode,
  useCallback,
  useContext,
} from 'react';

import { Combobox as ComboboxBase } from '@base-ui/react/combobox';
import { IconChevronDown, IconChevronUp } from '@tabler/icons-react';

import { type AnalyticsProps } from '../../helpers/analytics';
import { useImpressionRef } from '../../helpers/use-impression-ref';
import {
  type AccessibleNameProps,
  hasAccessibleName,
  useAccessibleNameWarning,
} from '../../utils/accessible-name';
import { cn } from '../../utils/cn';
import { usePortalContainer } from '../../utils/portal-provider';
import { type StripUndesiredProps } from '../../utils/strip-undesired-props';
import { Box } from '../box/box';
import { ATOMS_INPUT_VARIANTS, inputVariants } from '../input/input';

/** Combobox variant definitions. */
export const ATOMS_COMBOBOX_VARIANTS = {
  size: {
    xs: ATOMS_INPUT_VARIANTS.size.xs,
    sm: ATOMS_INPUT_VARIANTS.size.sm,
    md: ATOMS_INPUT_VARIANTS.size.base,
    lg: ATOMS_INPUT_VARIANTS.size.lg,
  },
} as const;

export const ATOMS_COMBOBOX_DEFAULT_VARIANTS = {
  size: 'md',
} as const;

export type ComboboxSize = keyof typeof ATOMS_COMBOBOX_VARIANTS.size;

// Size flows down to the styled Base UI parts via context so they don't need
// prop-drilling.
const ComboboxSizeContext = createContext<ComboboxSize>('md');

// Geometry mirrors bricks-next SearchableDropdown tokens.
const popupStyles: Record<
  ComboboxSize,
  { radius: string; padding: string; maxHeight: string; sideOffset: number }
> = {
  xs: { radius: 'rounded-xl', padding: 'p-1', maxHeight: 'max-h-44', sideOffset: 4 },
  sm: { radius: 'rounded-2xl', padding: 'p-1', maxHeight: 'max-h-60', sideOffset: 6 },
  md: { radius: 'rounded-3xl', padding: 'p-1.5', maxHeight: 'max-h-72', sideOffset: 8 },
  lg: { radius: 'rounded-3xl', padding: 'p-2', maxHeight: 'max-h-80', sideOffset: 10 },
};

// Item radius = popup radius − popup padding so highlights stay concentric
// with the popup's inner edge.
const itemStyles: Record<ComboboxSize, { padding: string; radius: string; typography: string }> = {
  xs: { padding: 'px-2.5 py-2.5', radius: 'rounded-lg', typography: 'text-body-md' },
  sm: { padding: 'p-4', radius: 'rounded-xl', typography: 'text-body-md' },
  md: { padding: 'p-5', radius: 'rounded-2xl', typography: 'text-body-lg' },
  lg: { padding: 'px-6 py-6', radius: 'rounded-2xl', typography: 'text-body-xl' },
};

const INPUT_SIZE: Record<ComboboxSize, keyof typeof ATOMS_INPUT_VARIANTS.size> = {
  xs: 'xs',
  sm: 'sm',
  md: 'base',
  lg: 'lg',
};

// Right offset for the chevron mirrors the input's horizontal text padding so
// the icon sits visually flush with the left-edge text padding.
const chevronRightOffset: Record<ComboboxSize, string> = {
  xs: 'right-2.5',
  sm: 'right-3',
  md: 'right-4',
  lg: 'right-5',
};

export interface ComboboxOption {
  value: string;
  label: string;
}

interface ComboboxBaseProps {
  size?: ComboboxSize;
  options: ComboboxOption[];
  value?: ComboboxOption | null;
  defaultValue?: ComboboxOption | null;
  onValueChange?: (value: ComboboxOption | null) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  name?: string;
  /** Whether the combobox is in an error state. Applies the error ring styling. */
  error?: boolean;
  /**
   * Ids of elements that describe the combobox (e.g. a hint or error message),
   * forwarded to the input as `aria-describedby`. Wired automatically inside a
   * `Field`/`Form`; set explicitly for standalone use. Mirrors `Input`.
   */
  'aria-describedby'?: string;
  /**
   * Marks the input invalid for assistive tech, forwarded as `aria-invalid`.
   * Wired automatically inside a `Field`/`Form`; set explicitly for standalone
   * use. Mirrors `Input`.
   */
  'aria-invalid'?: AriaAttributes['aria-invalid'];
}

// Requires an accessible name: an `aria-label`, an `aria-labelledby`, or an `id`
// referenced by an external `<label htmlFor>` (e.g. when composed in a `Field`).
export type ComboboxProps = ComboboxBaseProps & AccessibleNameProps & AnalyticsProps;

const StyledInput = forwardRef<
  HTMLInputElement,
  StripUndesiredProps<ComboboxBase.Input.Props> & { error?: boolean }
>(function StyledInput({ className, error = false, ...props }, ref) {
  const size = useContext(ComboboxSizeContext);
  return (
    <ComboboxBase.Input
      {...props}
      ref={ref}
      className={cn(
        inputVariants({ size: INPUT_SIZE[size], error }),
        'atoms-combobox-input group w-full disabled:cursor-not-allowed',
        // A committed value (Base UI `data-filled`) gets a darker outline so a
        // set filter reads as active, distinct from an empty/enabled one. Scoped
        // to enabled + non-error; the open/error rings below take precedence.
        !error && 'not-disabled:data-filled:ring-od-border-selected',
        error
          ? 'data-popup-open:ring-od-border-error data-popup-open:ring-2'
          : 'data-popup-open:ring-od-border-accent-brand data-popup-open:ring-2',
        className,
      )}
    />
  );
});

function StyledTrigger({ className, ...props }: StripUndesiredProps<ComboboxBase.Trigger.Props>) {
  return (
    <ComboboxBase.Trigger
      {...props}
      className={cn(
        'group m-0 cursor-pointer bg-transparent p-0 outline-none',
        'data-disabled:cursor-not-allowed data-disabled:opacity-50',
        className,
      )}
    />
  );
}

function StyledPopup({ className, ...props }: StripUndesiredProps<ComboboxBase.Popup.Props>) {
  const size = useContext(ComboboxSizeContext);
  const styles = popupStyles[size];
  return (
    <ComboboxBase.Popup
      {...props}
      className={cn(
        'flex flex-col',
        'max-w-(--available-width) min-w-(--anchor-width)',
        styles.maxHeight,
        styles.padding,
        styles.radius,
        'bg-od-bg-primary text-od-content-primary',
        'ring-2 ring-od-border-focused-accent-brand',
        className,
      )}
    />
  );
}

function StyledItem({ className, ...props }: StripUndesiredProps<ComboboxBase.Item.Props>) {
  const size = useContext(ComboboxSizeContext);
  const styles = itemStyles[size];
  return (
    <ComboboxBase.Item
      {...props}
      className={cn(
        'group cursor-pointer',
        styles.padding,
        styles.radius,
        styles.typography,
        'data-highlighted:bg-od-bg-hover-primary',
        'data-selected:bg-od-bg-hover-primary',
        'active:bg-od-bg-hover-tertiary',
        className,
      )}
    />
  );
}

function StyledEmpty({
  className,
  children,
  ...props
}: StripUndesiredProps<ComboboxBase.Empty.Props>) {
  const size = useContext(ComboboxSizeContext);
  const styles = itemStyles[size];
  return (
    <ComboboxBase.Empty
      {...props}
      className={cn(
        'shrink-0 text-od-content-inactive-secondary empty:m-0 empty:p-0',
        styles.padding,
        styles.typography,
        className,
      )}
    >
      {children}
    </ComboboxBase.Empty>
  );
}

const areOptionsEqual = (a: ComboboxOption, b: ComboboxOption) => a.value === b.value;

// ---------------------------------------------------------------------------
// Compound parts
//
// The default `Combobox` covers the closed select-with-filter case. These parts
// expose the same styling over Base UI's structure for cases it can't express:
// remote/async suggestions (`inputValue` + `filter={null}`), custom item
// rendering, a trailing slot, or extra content inside the popup. Every Base UI
// `Root` prop passes through, so controlled input text (`inputValue` /
// `onInputValueChange`) and controlled open state (`open` / `onOpenChange`) are
// available here but deliberately absent from the closed component.
// ---------------------------------------------------------------------------

export type ComboboxRootProps<T> = ComboboxBase.Root.Props<T> & {
  /** Size token propagated to `Input`, `Popup`, `List`, `Item`, and `Empty`. */
  size?: ComboboxSize;
};

/**
 * Second argument to `onInputValueChange` / `onOpenChange`. Inspect `reason` to
 * tell a user edit (`'input-change'`) from one Base UI made itself — it rewrites
 * the input text when the popup closes, which a controlled query needs to ignore.
 */
export type ComboboxChangeEventDetails = ComboboxBase.Root.ChangeEventDetails;
export type ComboboxChangeReason = ComboboxBase.Root.ChangeEventReason;

function ComboboxRoot<T>({ size = 'md', ...props }: ComboboxRootProps<T>) {
  return (
    <ComboboxSizeContext.Provider value={size}>
      <ComboboxBase.Root {...props} />
    </ComboboxSizeContext.Provider>
  );
}

export type ComboboxInputProps = StripUndesiredProps<ComboboxBase.Input.Props> & {
  error?: boolean;
  /**
   * Rendered at the input's trailing edge — a search icon, a loading spinner, or
   * an action button. Use `Combobox.Trigger` inside it for a popup toggle.
   */
  suffix?: ReactNode;
};

const ComboboxInput = forwardRef<HTMLInputElement, ComboboxInputProps>(function ComboboxInput(
  { suffix, className, ...props },
  ref,
) {
  const size = useContext(ComboboxSizeContext);

  if (!suffix) {
    return <StyledInput {...props} ref={ref} className={className} />;
  }

  return (
    <Box className="relative inline-block w-full">
      <StyledInput {...props} ref={ref} className={cn('pr-12', className)} />
      <Box
        className={cn(
          'absolute top-1/2 flex -translate-y-1/2 items-center justify-center',
          chevronRightOffset[size],
        )}
      >
        {suffix}
      </Box>
    </Box>
  );
});

export type ComboboxPopupProps = StripUndesiredProps<ComboboxBase.Popup.Props> & {
  align?: ComboboxBase.Positioner.Props['align'];
  sideOffset?: ComboboxBase.Positioner.Props['sideOffset'];
};

// Bundles Portal + Positioner so callers get the app's portal container and
// anchor sizing without restating them.
function ComboboxPopup({ align = 'start', sideOffset, ...props }: ComboboxPopupProps) {
  const size = useContext(ComboboxSizeContext);
  const portalContainer = usePortalContainer();
  return (
    <ComboboxBase.Portal container={portalContainer ?? undefined}>
      <ComboboxBase.Positioner
        align={align}
        sideOffset={sideOffset ?? popupStyles[size].sideOffset}
      >
        <StyledPopup {...props} />
      </ComboboxBase.Positioner>
    </ComboboxBase.Portal>
  );
}

export type ComboboxListProps = StripUndesiredProps<ComboboxBase.List.Props>;

function ComboboxList({ className, ...props }: ComboboxListProps) {
  const size = useContext(ComboboxSizeContext);
  return (
    <ComboboxBase.List
      {...props}
      className={cn(
        'min-h-0 flex-1 overflow-y-auto overscroll-contain scroll-pt-2 scroll-pb-2',
        itemStyles[size].radius,
        className,
      )}
    />
  );
}

const ComboboxSelect = forwardRef<HTMLInputElement, ComboboxProps>(function ComboboxSelect(
  {
    size = 'md',
    options,
    value,
    defaultValue,
    onValueChange,
    placeholder,
    disabled,
    className,
    name,
    error,
    id,
    analyticsName,
    trackImpression,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    'aria-describedby': ariaDescribedBy,
    'aria-invalid': ariaInvalid,
  },
  ref,
) {
  const portalContainer = usePortalContainer();

  useAccessibleNameWarning(
    'Combobox',
    hasAccessibleName({ 'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledBy, id }),
  );

  // View events only — typed/selected values are PII and never emitted. Matches
  // the Input/TextArea `inputViewEvent` contract; selection tracking is a
  // separate Tier 3 event pending journey-service sign-off. The consumer's
  // forwarded ref is merged in so callers can still reach the input.
  const viewRef = useImpressionRef<HTMLInputElement>(
    Boolean(analyticsName && trackImpression),
    analyticsName,
    'inputViewEvent',
    ref,
  );

  const handleValueChange = useCallback(
    (v: ComboboxOption | null) => onValueChange?.(v),
    [onValueChange],
  );

  return (
    <ComboboxSizeContext.Provider value={size}>
      <ComboboxBase.Root
        // `id` is set on the Root (not the Input) so Base UI keeps the input's
        // id tied to the combobox root id that wires the input↔popup. Setting it
        // directly on the Input decouples that wiring and the popup never opens.
        // The input still ends up with this id, so an external `<label htmlFor>`
        // (e.g. Field) stays associated.
        id={id}
        items={options}
        value={value}
        defaultValue={defaultValue}
        onValueChange={handleValueChange}
        disabled={disabled}
        name={name}
        isItemEqualToValue={areOptionsEqual}
      >
        <Box className={cn('relative inline-block w-full', className)}>
          <StyledInput
            ref={viewRef}
            placeholder={placeholder}
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledBy}
            aria-describedby={ariaDescribedBy}
            aria-invalid={ariaInvalid}
            error={error}
            className="pr-12"
          />
          <StyledTrigger
            aria-label="Show options"
            className={cn(
              'absolute top-1/2 flex -translate-y-1/2 items-center justify-center',
              chevronRightOffset[size],
            )}
          >
            <IconChevronDown
              size={16}
              stroke={3}
              className="text-od-content-primary group-data-popup-open:hidden"
            />
            <IconChevronUp
              size={16}
              stroke={3}
              className="hidden text-od-content-primary group-data-popup-open:block"
            />
          </StyledTrigger>
        </Box>
        <ComboboxBase.Portal container={portalContainer ?? undefined}>
          <ComboboxBase.Positioner align="start" sideOffset={popupStyles[size].sideOffset}>
            <StyledPopup>
              <StyledEmpty>No items found.</StyledEmpty>
              <ComboboxBase.List
                className={cn(
                  'min-h-0 flex-1 overflow-y-auto overscroll-contain scroll-pt-2 scroll-pb-2',
                  itemStyles[size].radius,
                )}
              >
                {(item: ComboboxOption) => (
                  <StyledItem key={item.value} value={item}>
                    {item.label}
                  </StyledItem>
                )}
              </ComboboxBase.List>
            </StyledPopup>
          </ComboboxBase.Positioner>
        </ComboboxBase.Portal>
      </ComboboxBase.Root>
    </ComboboxSizeContext.Provider>
  );
});

ComboboxSelect.displayName = 'Combobox';

/**
 * Searchable single-select dropdown. Pass `options`, wire `value` /
 * `onValueChange`, and you're done — DX parity with the bricks-next
 * `SearchableDropdown`.
 *
 * Filter behaviour: case-insensitive substring match on `label`. When the
 * query has no matches the popup shows "No items found."
 *
 * @example
 * ```tsx
 * <Combobox
 *   options={[{ value: 'apple', label: 'Apple' }]}
 *   value={value}
 *   onValueChange={setValue}
 *   placeholder="Pick a fruit"
 * />
 * ```
 *
 * Compose the parts instead when the suggestions come from a server, the items
 * need custom rendering, or the popup needs extra content. `filter={null}`
 * turns off client-side filtering so the list shows exactly what you pass.
 *
 * @example
 * ```tsx
 * <Combobox.Root
 *   items={predictions}
 *   filter={null}
 *   inputValue={query}
 *   onInputValueChange={setQuery}
 *   itemToStringLabel={(p) => p.description}
 * >
 *   <Combobox.Input suffix={isLoading ? <Loader /> : <IconSearch />} />
 *   <Combobox.Popup>
 *     <Combobox.List>
 *       {(p) => <Combobox.Item value={p}>{p.description}</Combobox.Item>}
 *     </Combobox.List>
 *   </Combobox.Popup>
 * </Combobox.Root>
 * ```
 */
export const Combobox = Object.assign(ComboboxSelect, {
  Root: ComboboxRoot,
  Input: ComboboxInput,
  Trigger: StyledTrigger,
  Popup: ComboboxPopup,
  List: ComboboxList,
  Item: StyledItem,
  Empty: StyledEmpty,
});
